import React from "react";
import { overlayBase, useParticles } from "./_shared";

interface Props {
  /** Number of snowflakes, default 22 */
  count?: number;
  /** Flake colour (rgba), default white */
  color?: string;
  /** Minimum flake diameter px, default 3 */
  minSize?: number;
  /** Maximum flake diameter px, default 7 */
  maxSize?: number;
  /** Minimum fall duration seconds, default 5 */
  minDur?: number;
  /** Maximum fall duration seconds, default 10 */
  maxDur?: number;
}

/**
 * Snow — drifting snowflakes falling with a side-to-side sway animation.
 */
const Snow: React.FC<Props> = ({
  count = 22,
  color = "rgba(255,255,255,0.85)",
  minSize = 3,
  maxSize = 7,
  minDur = 5,
  maxDur = 10,
}) => {
  const parts = useParticles(
    count,
    (i, r) => ({
      id: i,
      left: r(0) * 100,
      size: minSize + r(1) * (maxSize - minSize),
      dur: minDur + r(2) * (maxDur - minDur),
      delay: r(3) * maxDur,
      sway: 12 + r(4) * 18,
    }),
    [count, minSize, maxSize, minDur, maxDur]
  );

  return (
    <div style={overlayBase}>
      <style>{`
        @keyframes oot-snow-fall {
          0%   { transform: translateY(-10px); opacity: 0; }
          10%  { opacity: 1; }
          100% { transform: translateY(105vh); opacity: 0.9; }
        }
        @keyframes oot-snow-sway {
          0%, 100% { margin-left: calc(var(--sway) * -1); }
          50%       { margin-left: var(--sway); }
        }
      `}</style>
      {parts.map((p) => (
        <div
          key={p.id}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            top: 0,
            animation: `oot-snow-fall ${p.dur}s linear ${p.delay}s infinite`,
          }}
        >
          <div
            style={{
              width: p.size,
              height: p.size,
              borderRadius: "50%",
              background: color,
              animation: `oot-snow-sway ${p.dur / 3}s ease-in-out infinite`,
              "--sway": `${p.sway}px`,
            } as React.CSSProperties}
          />
        </div>
      ))}
    </div>
  );
};

export default Snow;
